import { supabase } from '../config/supabase';
import {
  getClinicalHistoryType,
  isClinicalHistoryRecord,
  isInitialAnamnesisRecord,
  type ClinicalHistoryType,
} from '../utils/clinicalDocuments';
import { getMedicalRecordAttachmentsObject, isMedicalRecordInvalidated } from '../utils/medicalRecordStatus';
import { getAssessmentV2ResultLabel } from '../features/assessments-v2/registry/assessmentRegistry';

export type PatientClinicalEventType =
  | 'exacerbation'
  | 'worsening'
  | 'complication'
  | 'hospitalization'
  | 'hospital_discharge'
  | 'treatment_discharge'
  | 'fall'
  | 'medication_change'
  | 'improvement'
  | 'other';

export const PATIENT_CLINICAL_EVENT_LABELS: Record<PatientClinicalEventType, string> = {
  exacerbation: 'Exacerbação',
  worsening: 'Piora clínica',
  complication: 'Intercorrência',
  hospitalization: 'Hospitalização',
  hospital_discharge: 'Alta hospitalar',
  treatment_discharge: 'Alta do tratamento',
  fall: 'Queda',
  medication_change: 'Mudança de medicação',
  improvement: 'Melhora relevante',
  other: 'Outro evento clínico',
};

export type PatientClinicalEventSeverity = 'low' | 'moderate' | 'high';

export interface PatientClinicalEventTypeOption {
  value: PatientClinicalEventType;
  label: string;
  defaultSeverity: PatientClinicalEventSeverity;
}

export const PATIENT_CLINICAL_EVENT_TYPE_OPTIONS: PatientClinicalEventTypeOption[] = [
  { value: 'hospitalization', label: PATIENT_CLINICAL_EVENT_LABELS.hospitalization, defaultSeverity: 'high' },
  { value: 'exacerbation', label: PATIENT_CLINICAL_EVENT_LABELS.exacerbation, defaultSeverity: 'high' },
  { value: 'worsening', label: PATIENT_CLINICAL_EVENT_LABELS.worsening, defaultSeverity: 'moderate' },
  { value: 'complication', label: PATIENT_CLINICAL_EVENT_LABELS.complication, defaultSeverity: 'moderate' },
  { value: 'fall', label: PATIENT_CLINICAL_EVENT_LABELS.fall, defaultSeverity: 'moderate' },
  { value: 'medication_change', label: PATIENT_CLINICAL_EVENT_LABELS.medication_change, defaultSeverity: 'moderate' },
  { value: 'improvement', label: PATIENT_CLINICAL_EVENT_LABELS.improvement, defaultSeverity: 'low' },
  { value: 'hospital_discharge', label: PATIENT_CLINICAL_EVENT_LABELS.hospital_discharge, defaultSeverity: 'low' },
  { value: 'treatment_discharge', label: PATIENT_CLINICAL_EVENT_LABELS.treatment_discharge, defaultSeverity: 'low' },
  { value: 'other', label: PATIENT_CLINICAL_EVENT_LABELS.other, defaultSeverity: 'moderate' },
];

export interface PatientClinicalEvent {
  eventId: string;
  clientId: string;
  patientId: string;
  eventType: PatientClinicalEventType;
  severity: PatientClinicalEventSeverity;
  eventTitle: string;
  eventDescription: string;
  eventDate: string;
  createdByUserId?: string | null;
  createdByDisplayName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PatientTimelineItem {
  id: string;
  source: 'clinical_event' | 'medical_record' | 'assessment_result_v2';
  kind: PatientClinicalEventType | 'initial_anamnesis' | 'clinical_history' | 'evaluation' | 'exam';
  title: string;
  description: string;
  date: string;
  severity?: PatientClinicalEventSeverity;
  authorName?: string | null;
  documentVersion?: number;
  clinicalHistoryType?: ClinicalHistoryType;
  recordAttachments?: unknown;
  assessmentResultIds?: string[];
}

interface CreateClinicalEventInput {
  clientId: string;
  patientId: string;
  eventType: PatientClinicalEventType;
  severity: PatientClinicalEventSeverity;
  eventTitle: string;
  eventDescription: string;
  eventDate: string;
  createdByUserId: string;
  createdByDisplayName?: string | null;
}

interface UpdateClinicalEventInput {
  eventId: string;
  clientId: string;
  patientId: string;
  eventType: PatientClinicalEventType;
  severity: PatientClinicalEventSeverity;
  eventTitle: string;
  eventDescription: string;
  eventDate: string;
}

interface TimelineMedicalRecord {
  recordId: string;
  recordType: string;
  recordTitle?: string | null;
  recordDescription?: string | null;
  recordDate: string;
  recordAttachments?: unknown;
}

interface TimelineAssessmentResult {
  resultId: string;
  sessionId: string;
  testKey: string;
  assessmentDate: string;
  status?: string | null;
  notes?: string | null;
  summaryJson?: Record<string, unknown> | null;
  professionalNameSnapshot?: string | null;
  legacySourceRecordId?: string | null;
}

const formatTimelineNumber = (value: number, suffix: string) =>
  `${Number.isInteger(value) ? value : value.toFixed(1)}${suffix}`;

const summarizeAssessmentResult = (result: TimelineAssessmentResult): string => {
  const summary = result.summaryJson && typeof result.summaryJson === 'object'
    ? result.summaryJson
    : {};
  const parts: string[] = [];

  const metricCandidates: Array<[unknown, string]> = [
    [summary.bestDistance_m, ' m'],
    [summary.bestDistanceMeters, ' m'],
    [summary.timeSeconds, ' s'],
    [summary.speedMs, ' m/s'],
    [summary.bestOverallKg, ' kg'],
    [summary.totalSteps, ' passos'],
    [summary.total, ' pontos'],
    [summary.percentage, '%'],
    [summary.grade, ''],
  ];
  const metric = metricCandidates.find(([value]) => typeof value === 'number');
  if (metric && typeof metric[0] === 'number') {
    parts.push(formatTimelineNumber(metric[0], metric[1]));
  }

  const percentPredicted = summary.percentPredicted;
  if (typeof percentPredicted === 'number') {
    parts.push(`${formatTimelineNumber(percentPredicted, '%')} do previsto`);
  }

  const classification = [summary.classification, summary.severity, summary.normativeBandLabel]
    .find((value) => typeof value === 'string' && value.trim().length > 0);
  if (typeof classification === 'string') parts.push(classification.trim());

  if (parts.length > 0) return parts.join(' · ');
  if (result.notes?.trim()) return result.notes.trim();
  return 'Resultado concluído.';
};

export class PatientClinicalTimelineService {
  static async getTimeline(patientId: string, clientId: string): Promise<PatientTimelineItem[]> {
    const [eventsResult, recordsResult, assessmentsResult] = await Promise.all([
      supabase
        .from('patient_clinical_events')
        .select('*')
        .eq('clientId', clientId)
        .eq('patientId', patientId)
        .order('eventDate', { ascending: false }),
      supabase
        .from('medical_records')
        .select('recordId, recordType, recordTitle, recordDescription, recordDate, recordAttachments')
        .eq('clientId', clientId)
        .eq('patientId', patientId)
        .order('recordDate', { ascending: false }),
      supabase
        .from('assessment_results_v2')
        .select('resultId, sessionId, testKey, assessmentDate, status, notes, summaryJson, professionalNameSnapshot, legacySourceRecordId')
        .eq('clientId', clientId)
        .eq('patientId', patientId)
        .order('assessmentDate', { ascending: false }),
    ]);

    if (eventsResult.error) throw eventsResult.error;
    if (recordsResult.error) throw recordsResult.error;
    if (assessmentsResult.error) throw assessmentsResult.error;

    const eventItems: PatientTimelineItem[] = ((eventsResult.data || []) as PatientClinicalEvent[]).map(
      (event) => ({
        id: event.eventId,
        source: 'clinical_event',
        kind: event.eventType,
        title: event.eventTitle,
        description: event.eventDescription,
        date: event.eventDate,
        severity: event.severity,
        authorName: event.createdByDisplayName,
      }),
    );

    const assessmentResults = ((assessmentsResult.data || []) as TimelineAssessmentResult[])
      .filter((result) => result.status !== 'draft' && result.status !== 'cancelled');
    const legacyAssessmentRecordIds = new Set(
      assessmentResults
        .map((result) => result.legacySourceRecordId)
        .filter((recordId): recordId is string => Boolean(recordId)),
    );

    const medicalRecords = (recordsResult.data || []) as TimelineMedicalRecord[];
    const documentItems: PatientTimelineItem[] = medicalRecords
      .filter((record) => !isMedicalRecordInvalidated(record.recordType, record.recordAttachments))
      .filter((record) => !legacyAssessmentRecordIds.has(record.recordId))
      .filter(
        (record) =>
          isClinicalHistoryRecord(record.recordAttachments) ||
          record.recordType === 'evaluation' ||
          record.recordType === 'exam',
      )
      .map((record) => {
        const attachments = getMedicalRecordAttachmentsObject(record.recordAttachments);
        const documentVersion = Number(attachments.clinicalDocumentVersion);

        return {
          id: record.recordId,
          source: 'medical_record' as const,
          kind: isInitialAnamnesisRecord(record.recordAttachments)
            ? ('initial_anamnesis' as const)
            : isClinicalHistoryRecord(record.recordAttachments)
              ? ('clinical_history' as const)
              : (record.recordType as 'evaluation' | 'exam'),
          title: record.recordTitle || (record.recordType === 'exam' ? 'Exame' : 'Avaliação'),
          description: record.recordDescription || 'Sem descrição registrada.',
          date: record.recordDate,
          documentVersion: Number.isFinite(documentVersion) && documentVersion > 0
            ? documentVersion
            : undefined,
          clinicalHistoryType: isClinicalHistoryRecord(record.recordAttachments)
            ? getClinicalHistoryType(record.recordAttachments)
            : undefined,
          recordAttachments: record.recordType === 'exam'
            ? record.recordAttachments
            : undefined,
        };
      });

    const resultsBySession = new Map<string, TimelineAssessmentResult[]>();
    assessmentResults.forEach((result) => {
      const sessionKey = result.sessionId || result.resultId;
      const current = resultsBySession.get(sessionKey) || [];
      current.push(result);
      resultsBySession.set(sessionKey, current);
    });

    const assessmentItems: PatientTimelineItem[] = Array.from(resultsBySession.entries()).map(
      ([sessionId, sessionResults]) => {
        const orderedResults = [...sessionResults].sort(
          (left, right) => new Date(left.assessmentDate).getTime() - new Date(right.assessmentDate).getTime(),
        );
        const labels = orderedResults.map((result) => getAssessmentV2ResultLabel(result));
        const descriptions = orderedResults.map(
          (result) => `${getAssessmentV2ResultLabel(result)}: ${summarizeAssessmentResult(result)}`,
        );

        return {
          id: sessionId,
          source: 'assessment_result_v2' as const,
          kind: 'evaluation' as const,
          title: labels.length === 1 ? labels[0] : `Avaliação clínica · ${labels.length} testes`,
          description: descriptions.join('\n'),
          date: orderedResults[0]?.assessmentDate || new Date().toISOString(),
          authorName: orderedResults.find((result) => result.professionalNameSnapshot)?.professionalNameSnapshot,
          assessmentResultIds: orderedResults.map((result) => result.resultId),
        };
      },
    );

    return [...eventItems, ...documentItems, ...assessmentItems].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }

  static async createEvent(input: CreateClinicalEventInput): Promise<PatientClinicalEvent> {
    const { data, error } = await supabase
      .from('patient_clinical_events')
      .insert({
        ...input,
        eventTitle: input.eventTitle.trim(),
        eventDescription: input.eventDescription.trim(),
        eventDate: new Date(input.eventDate).toISOString(),
      })
      .select('*')
      .single();

    if (error) throw error;
    return data as PatientClinicalEvent;
  }

  static async updateEvent(input: UpdateClinicalEventInput): Promise<PatientClinicalEvent> {
    const { data, error } = await supabase
      .from('patient_clinical_events')
      .update({
        eventType: input.eventType,
        severity: input.severity,
        eventTitle: input.eventTitle.trim(),
        eventDescription: input.eventDescription.trim(),
        eventDate: new Date(input.eventDate).toISOString(),
      })
      .eq('eventId', input.eventId)
      .eq('clientId', input.clientId)
      .eq('patientId', input.patientId)
      .select('*')
      .single();

    if (error) throw error;
    return data as PatientClinicalEvent;
  }
}
